import {
  PRESS_PHOTO_VARIANTS,
  PROFILE_BANNER_KINDS,
  PROFILE_DEFAULT_FOCUS,
  profileDefaultSource,
  profileImageSpec,
  profileSourceMoves,
  profileSourceNeeds,
  profileSourcesOf,
  type ArtistProfileKind,
  type ArtistProfileSource,
  type ProfileMakeItem,
  type SerializedProfileAsset,
} from 'fym-shared/src/music24/artistProfile';
import {
  isProfileTextKind,
  PROFILE_BANNER_TEXT_DEFAULT,
  type ProfileBannerText,
} from 'fym-shared/src/music24/profileBanner';
import type { ProfileBannerKind, ProfileImageTool } from '../profileRoute';

/** The choice of a tool before "Make it" (design b1–b4). It lives in memory; the tool lives in the URL. */
export interface ProfileToolDraft {
  source: ArtistProfileSource;
  /** The release of the cover (and of "Use the look of this release"). */
  catalogItemUuid: string | undefined;
  /** "Use the look of this release" (design b1): the theme and colours of the release kit. */
  lookFromRelease: boolean;
  /** `photo`: the file and its address for the preview. */
  fileId: number | undefined;
  photoUrl: string | undefined;
  /** `bank`, `ai`: the picture. */
  pictureUuid: string | undefined;
  focusX: number;
  focusY: number;
  /** The artist photo: also set it as the Music24 artist photo (design b3). */
  setAsAccountAvatar: boolean;
  /** The banners tool: one Make makes both banners with one picture (design b2). */
  sameForAll: boolean;
  /** The banners tool: the text on both banners (design profile-card b1; one choice sets both). */
  bannerText: ProfileBannerText;
}

/** The kind that a tool shows and makes first (the banners tool: its tab). Pure. */
export function toolKind(tool: ProfileImageTool, banner: ProfileBannerKind): ArtistProfileKind {
  return tool === 'banners' ? banner : tool;
}

/**
 * The first draft of a tool: the picture of the current version when it has one (its source, its
 * release, its photo or picture, its focus), else the default source of the kind with the newest
 * release. Pure.
 */
export function initialToolDraft(
  kind: ArtistProfileKind,
  current: SerializedProfileAsset | null,
  newestReleaseUuid: string | undefined,
  bannerText: ProfileBannerText = PROFILE_BANNER_TEXT_DEFAULT
): ProfileToolDraft {
  const sources = profileSourcesOf(kind);
  const source = current !== null && sources.includes(current.source) ? current.source : profileDefaultSource(kind);
  const fromCurrent = current !== null && current.source === source;
  return {
    source,
    catalogItemUuid: (fromCurrent ? current.catalogItemUuid : null) ?? newestReleaseUuid,
    lookFromRelease: fromCurrent && current.look.from === 'release',
    fileId: fromCurrent && source === 'photo' ? (current.fileId ?? undefined) : undefined,
    photoUrl: undefined,
    pictureUuid: fromCurrent && (source === 'bank' || source === 'ai') ? (current.pictureUuid ?? undefined) : undefined,
    focusX: (fromCurrent ? current.focusX : null) ?? PROFILE_DEFAULT_FOCUS.x,
    focusY: (fromCurrent ? current.focusY : null) ?? PROFILE_DEFAULT_FOCUS.y,
    setAsAccountAvatar: false,
    sameForAll: true,
    bannerText,
  };
}

/** Why "Make it" cannot run yet: the id that the source needs is missing. Pure. */
export function toolDraftBlocker(draft: ProfileToolDraft): 'release' | 'photo' | 'picture' | null {
  const needs = profileSourceNeeds(draft.source);
  if (needs === 'release' && draft.catalogItemUuid === undefined) {
    return 'release';
  }
  if (needs === 'file' && draft.fileId === undefined) {
    return 'photo';
  }
  if (needs === 'picture' && draft.pictureUuid === undefined) {
    return 'picture';
  }
  return null;
}

/** True when "Use the look of this release" applies: a blurred cover of a picked release. Pure. */
export function usesReleaseLook(kind: ArtistProfileKind, draft: ProfileToolDraft): boolean {
  return (
    draft.lookFromRelease &&
    draft.source === 'cover' &&
    draft.catalogItemUuid !== undefined &&
    profileImageSpec(kind).coverMode === 'blur'
  );
}

/**
 * The make item of one kind from the draft (`POST .../artist-profile/assets`): only the keys of the
 * picked source, the focus only for a picture that moves, the look, and the Music24 photo switch of
 * the artist photo. The press photo sends no variant: one make makes the three crops. Pure.
 */
export function profileMakeItem(kind: ArtistProfileKind, draft: ProfileToolDraft): ProfileMakeItem {
  const item: ProfileMakeItem = { kind, source: draft.source };
  const needs = profileSourceNeeds(draft.source);
  if (needs === 'release' && draft.catalogItemUuid !== undefined) {
    item.catalogItemUuid = draft.catalogItemUuid;
  }
  if (needs === 'file' && draft.fileId !== undefined) {
    item.fileId = draft.fileId;
  }
  if (needs === 'picture' && draft.pictureUuid !== undefined) {
    item.pictureUuid = draft.pictureUuid;
  }
  if (profileSourceMoves(kind, draft.source)) {
    item.focusX = draft.focusX;
    item.focusY = draft.focusY;
  }
  item.lookFrom = usesReleaseLook(kind, draft) ? 'release' : 'artist';
  if (kind === 'spotifyAvatar' && draft.setAsAccountAvatar) {
    item.setAsAccountAvatar = true;
  }
  // The Spotify header never has text (Spotify asks for header images with no text).
  if (isProfileTextKind(kind)) {
    item.bannerText = draft.bannerText;
  }
  return item;
}

/**
 * The items that "Make it" / "Make both" / "Make all three" sends (design b1–b4). The banners tool
 * sends both banners with one picture while "Same picture for both" is on, else only its tab. Pure.
 */
export function profileToolItems(
  tool: ProfileImageTool,
  banner: ProfileBannerKind,
  draft: ProfileToolDraft
): ProfileMakeItem[] {
  if (tool === 'banners') {
    const kinds = draft.sameForAll ? PROFILE_BANNER_KINDS : [banner];
    return kinds.map((kind) => profileMakeItem(kind, draft));
  }
  return [profileMakeItem(tool, draft)];
}

/**
 * How many files one click of "Make" makes: the three press crops ("Make all three"), both banners
 * while "Same picture for both" is on ("Make both"), else one file. Pure.
 */
export function filesPerMake(tool: ProfileImageTool, draft: ProfileToolDraft): number {
  if (tool === 'pressPhoto') {
    return PRESS_PHOTO_VARIANTS.length;
  }
  if (tool === 'banners' && draft.sameForAll) {
    return PROFILE_BANNER_KINDS.length;
  }
  return 1;
}
